import React, { Fragment, useMemo } from 'react';

import { getCurrentCountry } from '@front/common/src/utils/country';
import { useModalObject } from '@front/common/src/hooks/use-modal';

import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import { getFormattedPriceForMultiple, getFormattedPrice } from '@front/customer/src/utils/cleaning';
import { StandardPrices } from '@front/customer/src/types/prices';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import TextButton from '@front/web-ui/src/components/button/text-button';
import Separator from '@front/web-ui/src/components/separator/separator';

import { useWhatIsTaxCreditModals } from '../../../../../screens/instant-tax-credit-selection/use-what-is-tax-credit-modals';
import TaxModal from '../tax-modal';

import { PriceLine } from './price-line';

const t = translate('basket');

type PriceLinesProps = {
  duration: number | null;
  shouldShowTaxCreditPriceInfo: boolean;
  isITCSelectedOrSuccessfullyPaired: boolean;
  isLoading: boolean;
  isMultiple: boolean;
  prices?: StandardPrices;
};

export const PriceLines = ({
  duration,
  shouldShowTaxCreditPriceInfo,
  isITCSelectedOrSuccessfullyPaired,
  isLoading,
  isMultiple,
  prices,
}: PriceLinesProps) => {
  const { hasTaxCreditInBasket, shouldShowInstantTaxCreditSelectionScreen } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  // Tax credit only applies when booking for self (legal eligibility)
  const showTaxCreditInBasket = hasTaxCreditInBasket && shouldShowTaxCreditPriceInfo;

  const { isOpen: isTaxCreditModalOpen, open: openTaxCreditModal, close: closeTaxCreditModal } = useModalObject();
  const { whatIsTaxCreditModals, openWhatIsInstantTaxCreditModal, openWhatIsTaxCreditModal } =
    useWhatIsTaxCreditModals();

  const formattedTotalPrice = isMultiple
    ? getFormattedPriceForMultiple(!!duration, prices?.total)
    : getFormattedPrice(!!duration, prices?.total);

  const formattedPriceAfterTax = isMultiple
    ? getFormattedPriceForMultiple(!!duration, prices?.after_tax)
    : getFormattedPrice(!!duration, prices?.after_tax);

  const openWhatIsTaxModals = isITCSelectedOrSuccessfullyPaired
    ? openWhatIsInstantTaxCreditModal
    : openWhatIsTaxCreditModal;

  const openModal = shouldShowInstantTaxCreditSelectionScreen ? openWhatIsTaxModals : openTaxCreditModal;

  const line1Label = isITCSelectedOrSuccessfullyPaired ? t('.subtotal') : t('.total');
  const line1: [string, React.ReactNode, React.ReactNode] = [
    'line1',
    <Text>{line1Label}</Text>,
    <Text strikedOut={isITCSelectedOrSuccessfullyPaired}>{formattedTotalPrice}</Text>,
  ];

  const priceLines: Array<[string, React.ReactNode, React.ReactNode]> = [line1];

  if (showTaxCreditInBasket) {
    const line2Label = isITCSelectedOrSuccessfullyPaired
      ? t('.cleaning.services.tax.label_itc')
      : t('.cleaning.services.tax.label');

    const line2: [string, React.ReactNode, React.ReactNode] = [
      'line2',
      <TextButton onClick={openModal} label={line2Label}>
        {line2Label}
      </TextButton>,
      <Text>{`- ${getFormattedPrice(!!duration, prices?.tax_credit)}`}</Text>,
    ];

    const line3Label = isITCSelectedOrSuccessfullyPaired ? t('.total') : t('.after_tax_credit');
    const line3: [string, React.ReactNode, React.ReactNode] = [
      'line3',
      <Text.MDBold>{line3Label}</Text.MDBold>,
      <Text.MDBold>{formattedPriceAfterTax}</Text.MDBold>,
    ];

    priceLines.push(line2, line3);
  }

  const modals = useMemo(
    () =>
      shouldShowInstantTaxCreditSelectionScreen ? (
        whatIsTaxCreditModals
      ) : (
        <TaxModal onClose={closeTaxCreditModal} isOpen={isTaxCreditModalOpen} />
      ),
    [shouldShowInstantTaxCreditSelectionScreen, whatIsTaxCreditModals, closeTaxCreditModal, isTaxCreditModalOpen],
  );

  return (
    <>
      {modals}
      <Separator />
      {priceLines.map(([key, label, value], index) => (
        <Fragment key={key}>
          <PriceLine label={label} value={value} isLoading={isLoading} />
          {index !== priceLines.length - 1 && <Separator />}
        </Fragment>
      ))}
    </>
  );
};
